import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { Tables, TablesInsert, TablesUpdate } from '@/integrations/supabase/types'

// Types pour blog_posts
type BlogPost = Tables<'blog_posts'>
type BlogPostInsert = TablesInsert<'blog_posts'>
type BlogPostUpdate = TablesUpdate<'blog_posts'>

// Interface étendue pour l'insertion avec gestion des tags flexibles
interface BlogPostCreateData {
  title: string
  slug: string
  excerpt: string
  content: string
  image_url: string
  category: string
  tags: string | string[]
  status?: 'draft' | 'published'
  author_id: string
  read_time: number
}

// Interface étendue pour la mise à jour avec gestion des tags flexibles  
interface BlogPostUpdateData {
  title?: string
  slug?: string
  excerpt?: string
  content?: string
  image_url?: string
  category?: string
  tags?: string | string[]
  status?: 'draft' | 'published'
  author_id?: string
  read_time?: number
}

export const useBlogPosts = (status?: 'published' | 'draft') => {
  return useQuery({
    queryKey: ['blog-posts', status],
    queryFn: async () => {
      let query = supabase
        .from('blog_posts')
        .select('*')
        .order('created_at', { ascending: false })

      if (status) {
        query = query.eq('status', status)
      }

      const { data, error } = await query
      if (error) {
        console.error('Erreur lors de la récupération des articles:', error)
        throw error
      }
      return data || []
    },
  })
}

export const useBlogPost = (slug: string, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: ['blog-post', slug],
    queryFn: async () => {
      // Si le slug ressemble à un UUID, recherche par ID, sinon par slug
      const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(slug)
      
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .eq(isUuid ? 'id' : 'slug', slug)
        .single()

      if (error) {
        console.error('Erreur lors de la récupération de l\'article:', error)
        throw error
      }
      return data
    },
    enabled: !!slug && (options?.enabled !== false),
  })
}

export const useCreateBlogPost = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (post: BlogPostCreateData) => {
      console.log('🚀 Tentative de création d\'article:', post)
      
      // Normaliser les tags en string
      const normalizedTags = Array.isArray(post.tags) ? post.tags.join(', ') : post.tags
      
      // Préparer les données pour Supabase
      const articleData: BlogPostInsert = {
        title: post.title,
        slug: post.slug,
        excerpt: post.excerpt,
        content: post.content,
        image_url: post.image_url,
        category: post.category,
        tags: normalizedTags,
        status: post.status || 'draft',
        author_id: post.author_id,
        read_time: post.read_time,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        published_at: post.status === 'published' ? new Date().toISOString() : null
      }
      
      console.log('📝 Données normalisées:', articleData)

      const { data, error } = await supabase
        .from('blog_posts')
        .insert(articleData)
        .select()
        .single()

      if (error) {
        console.error('❌ Erreur lors de la création:', error)
        // Messages d'erreur plus explicites
        if (error.message.includes('row-level security')) {
          throw new Error('Erreur de permissions. Vérifiez que vous êtes connecté et autorisé à créer des articles.')
        }
        if (error.message.includes('duplicate key')) {
          throw new Error('Un article avec ce slug existe déjà. Modifiez le titre ou le slug.')
        }
        throw new Error(`Erreur lors de la création: ${error.message}`)
      }

      console.log('✅ Article créé avec succès:', data)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blog-posts'] })
      console.log('🔄 Cache mis à jour')
    },
    onError: (error) => {
      console.error('💥 Erreur mutation:', error)
    }
  })
}

export const useUpdateBlogPost = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, ...updates }: BlogPostUpdateData & { id: string }) => {
      console.log('🔄 Tentative de mise à jour d\'article:', { id, updates })
      
      // Normaliser les tags en string si présents
      const normalizedTags = updates.tags 
        ? Array.isArray(updates.tags) ? updates.tags.join(', ') : updates.tags
        : undefined
      
      // Préparer les données pour Supabase
      const normalizedUpdates: BlogPostUpdate = {
        ...updates,
        tags: normalizedTags,
        updated_at: new Date().toISOString(),
        // Gérer published_at selon le statut
        ...(updates.status === 'published' && { published_at: new Date().toISOString() }),
        ...(updates.status === 'draft' && { published_at: null })
      }

      const { data, error } = await supabase
        .from('blog_posts')
        .update(normalizedUpdates)
        .eq('id', id)
        .select()
        .single()

      if (error) {
        console.error('❌ Erreur lors de la mise à jour:', error)
        if (error.message.includes('row-level security')) {
          throw new Error('Erreur de permissions. Vérifiez que vous êtes autorisé à modifier cet article.')
        }
        throw new Error(`Erreur lors de la mise à jour: ${error.message}`)
      }

      console.log('✅ Article mis à jour avec succès:', data)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blog-posts'] })
      console.log('🔄 Cache mis à jour')
    },
  })
}

export const useDeleteBlogPost = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      console.log('🗑️ Tentative de suppression d\'article:', id)
      
      const { error } = await supabase
        .from('blog_posts')
        .delete()
        .eq('id', id)

      if (error) {
        console.error('❌ Erreur lors de la suppression:', error)
        if (error.message.includes('row-level security')) {
          throw new Error('Erreur de permissions. Vérifiez que vous êtes autorisé à supprimer cet article.')
        }
        throw new Error(`Erreur lors de la suppression: ${error.message}`)
      }

      console.log('✅ Article supprimé avec succès')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blog-posts'] })
      console.log('🔄 Cache mis à jour')
    },
  })
}
